/**
 * Character styling for reference-table cells.
 *
 * A cell carries its emphasis in a single `styling` string. That string used to
 * hold exactly one value, which is what made the three toolbar buttons mutually
 * exclusive — writing 'underline' erased 'bold'. It is now a space-separated
 * set, so 'bold underline' is expressible.
 *
 * Space-separated rather than a `string[]` on purpose: every stored document,
 * the zod schema in the document service (`styling: z.string().optional()`) and
 * the generated API contract already type this as a plain string. Keeping it a
 * string means old single-value data reads back correctly with no migration and
 * no contract change — 'bold' parses to exactly the set it always meant.
 *
 * This lives in `shared/lib` because all three consumers need it and they sit on
 * different sides of the boundary rules (shared -> shared only): the sheet
 * renderer is in `shared`, the compare/diff renderer in `app`, the toolbar in
 * `features`, and the selection predicate in `entities`.
 */

export type CellStyling = 'bold' | 'italic' | 'underline';

/**
 * The classes each emphasis paints with. `underline` has no table-specific
 * variant — matching the original renderer, which emitted only `xrp-underline`.
 */
const STYLING_CLASSES: Record<CellStyling, string> = {
  bold: 'xrp-bold xrp-table-bold',
  italic: 'xrp-italic xrp-table-italic',
  underline: 'xrp-underline',
};

/**
 * Canonical order. Parsing and serialising both run through it, so a cell's
 * stored value and emitted class list don't depend on which order the buttons
 * were clicked — 'bold underline' never also exists as 'underline bold'.
 */
const STYLING_ORDER = Object.keys(STYLING_CLASSES) as CellStyling[];

/**
 * Unknown tokens are dropped. That matches the old renderer, which compared
 * `cell.styling` against the three known values and rendered nothing otherwise,
 * and it keeps arbitrary stored strings out of the emitted `class` attribute.
 */
export function parseCellStyling(styling?: string): CellStyling[] {
  if (!styling) return [];
  const tokens = new Set(styling.split(/\s+/).filter(Boolean));
  return STYLING_ORDER.filter((styleName) => tokens.has(styleName));
}

export function hasCellStyling(styling: string | undefined, value: string): boolean {
  return parseCellStyling(styling).includes(value as CellStyling);
}

/**
 * Add or remove one emphasis, leaving the others intact.
 *
 * `enabled` is passed in rather than flipped here because the toolbar decides
 * once for the whole selection (all-or-nothing) and then applies that same
 * decision to every cell — a per-cell flip would scramble a mixed selection.
 *
 * Returns `undefined` when nothing is left, so callers can drop the key instead
 * of persisting an empty string.
 */
export function withCellStyling(styling: string | undefined, value: string, enabled: boolean): string | undefined {
  const next = new Set(parseCellStyling(styling));

  if (enabled) {
    next.add(value as CellStyling);
  } else {
    next.delete(value as CellStyling);
  }

  const ordered = STYLING_ORDER.filter((styleName) => next.has(styleName));

  return ordered.length ? ordered.join(' ') : undefined;
}

/** The space-separated class list for a cell's styling; '' when it has none. */
export function cellStylingClassNames(styling?: string): string {
  return parseCellStyling(styling)
    .map((styleName) => STYLING_CLASSES[styleName])
    .join(' ');
}
